import { XCircleIcon } from "@heroicons/react/20/solid"
import Alert from "@src/components/Alert"
import Button from "@src/components/Button"
import { Dialog } from "@src/components/Dialog"
import {
  HelpScoutArticleKey,
  openHelpScoutArticle,
} from "@src/config/helpScoutArticles"
import { useWebAuthnUIStore } from "@src/features/webauthn/hooks/useWebAuthnUiStore"
import { PasskeyIcon } from "@src/icons"
import clsx from "clsx"
import { useForm } from "react-hook-form"

interface FormData {
  passkeyName: string
}

export function WebAuthnDialog() {
  const webauthnUI = useWebAuthnUIStore()
  const showCreateForm = webauthnUI.mode === "create"
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      passkeyName: "My near.com account",
    },
  })
  const passkeyName = watch("passkeyName")

  // Don't render the portal when closed to avoid orphaned DOM elements
  // during navigation that can block interaction
  if (!webauthnUI.isOpen) {
    return null
  }

  return (
    <Dialog
      open={true}
      onClose={webauthnUI.close}
      back={showCreateForm ? () => webauthnUI.setMode("choose") : undefined}
    >
      <div className="flex flex-col items-center justify-center">
        <div className="flex size-14 items-center justify-center rounded-full bg-gray-900 text-green-500 dark:bg-green-500/10">
          <PasskeyIcon className="size-7" />
        </div>
        <h2 className="mt-4! text-center font-bold text-gray-900 text-xl tracking-tight dark:text-white">
          {showCreateForm ? "Create new passkey" : "Continue with passkey"}
        </h2>
      </div>

      {showCreateForm ? (
        <form
          onSubmit={handleSubmit(({ passkeyName }) => {
            void webauthnUI.createNew(passkeyName)
          })}
          className="mt-6 space-y-2"
        >
          <label htmlFor="passkeyName" className="sr-only">
            Passkey label (only visible to you)
          </label>

          <div className="grid grid-cols-1">
            <input
              id="passkeyName"
              data-testid="webauthn-passkey-name"
              type="text"
              placeholder="Passkey label (only visible to you)"
              // biome-ignore lint/a11y/noAutofocus: This dialog only has one input, so autofocus is appropriate.
              autoFocus
              className={clsx(
                "focus-visible:-outline-offset-2 hover:-outline-offset-2 col-start-1 row-start-1 block w-full rounded-2xl bg-white px-3 py-4.5 font-semibold text-gray-900 text-sm/none placeholder:text-gray-400 hover:outline-2 focus-visible:outline-2 dark:bg-gray-850 dark:text-white",
                errors.passkeyName
                  ? "-outline-offset-2 outline-2 outline-red-500 hover:outline-red-500 focus-visible:outline-red-500 dark:outline-red-500/50 dark:focus-visible:outline-red-500 dark:hover:outline-red-500"
                  : "-outline-offset-1 outline-1 outline-gray-200 hover:outline-gray-400 focus-visible:outline-gray-400 dark:outline-white/5"
              )}
              {...register("passkeyName", { required: true })}
            />
            {passkeyName.length > 0 && (
              <button
                type="button"
                onClick={() => reset({ passkeyName: "" })}
                className="col-start-1 row-start-1 flex h-full shrink-0 flex-col items-center justify-center self-center justify-self-end rounded-r-2xl pr-3 pl-1 text-gray-500 hover:text-gray-900 focus-visible:text-gray-900 focus-visible:outline-none dark:text-gray-400 dark:focus-visible:text-white dark:hover:text-white"
              >
                <span className="sr-only">Clear</span>
                <XCircleIcon className="size-5" aria-hidden />
              </button>
            )}
          </div>

          <Button
            type="submit"
            size="xl"
            fullWidth
            loading={webauthnUI.isCreating}
            data-testid="webauthn-create-submit"
          >
            {webauthnUI.isCreating ? "Creating..." : "Create new passkey"}
          </Button>

          {webauthnUI.createError != null && (
            <Alert variant="error">{webauthnUI.createError}</Alert>
          )}

          <Alert variant="warning" className="mt-4">
            Store your passkeys securely. Losing your passkey means losing
            access to your account and any associated funds permanently.
          </Alert>
        </form>
      ) : (
        <div className="mt-6 space-y-2">
          <Button
            onClick={() => webauthnUI.signIn()}
            loading={webauthnUI.isSigningIn}
            size="xl"
            fullWidth
            data-testid="webauthn-sign-in"
          >
            {webauthnUI.isSigningIn ? "Connecting..." : "Use existing passkey"}
          </Button>

          <Button
            onClick={() => webauthnUI.setMode("create")}
            disabled={webauthnUI.isSigningIn}
            size="xl"
            fullWidth
            variant="secondary"
            data-testid="webauthn-create-new"
          >
            Create new passkey
          </Button>

          {webauthnUI.signInError != null && (
            <Alert variant="error">{webauthnUI.signInError}</Alert>
          )}

          {webauthnUI.signInHint != null && (
            <Alert variant="info">{webauthnUI.signInHint}</Alert>
          )}

          <p className="mt-4 text-center font-medium text-gray-500 text-sm dark:text-gray-400">
            Previously used near-intents.org?{" "}
            <button
              type="button"
              className="underline hover:text-gray-700 dark:hover:text-gray-300"
              onClick={() => {
                openHelpScoutArticle(HelpScoutArticleKey.PASSKEY_MIGRATION)
              }}
            >
              Learn more
            </button>
          </p>
        </div>
      )}
    </Dialog>
  )
}
